import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Sparkles,
  Upload,
  FileText,
  Loader2,
  CheckCircle2,
  RefreshCw,
  ThumbsUp,
  AlertCircle,
  ArrowRight,
  Lightbulb,
  Target,
  TrendingUp,
  Download,
  Link2,
  Copy,
  ExternalLink,
  Briefcase,
  GraduationCap,
  Award,
  Languages,
  User,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicJobs() {
  const { user, requireEmail } = useUser();
  const [jobDescription, setJobDescription] = useState("");
  const [jobUrl, setJobUrl] = useState("");
  const [inputMode, setInputMode] = useState("paste");
  const [extracting, setExtracting] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [hasProfileCV, setHasProfileCV] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [approving, setApproving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [optimization, setOptimization] = useState(null);
  const [showOptimization, setShowOptimization] = useState(false);
  const [optimizationStatus, setOptimizationStatus] = useState({
    attemptsUsed: 0,
    attemptsRemaining: 3,
    isApproved: false
  });

  useEffect(() => {
    if (user?.email) {
      checkProfileCV();
    }
  }, [user?.email]);

  const checkProfileCV = async () => {
    try {
      const response = await axios.get(`${API}/candidate/profile/${encodeURIComponent(user.email)}`);
      if (response.data?.cv_data) {
        setHasProfileCV(true);
      }
    } catch (error) {
      setHasProfileCV(false);
    }
  };

  const handleExtractFromUrl = async () => {
    if (!jobUrl.trim()) {
      toast.error("Por favor ingresa el enlace de la oferta de trabajo");
      return;
    }

    setExtracting(true);
    try {
      const response = await axios.post(`${API}/candidate/extract-job-description`, {
        url: jobUrl
      }, { timeout: 30000 });
      
      if (response.data.description) {
        setJobDescription(response.data.description);
        toast.success("¡Descripción extraída exitosamente!");
        setInputMode("paste");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "No pudimos extraer la descripción. Copia y pégala manualmente.");
    } finally {
      setExtracting(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const ext = file.name.toLowerCase().split(".").pop();
    if (!["pdf", "docx"].includes(ext)) {
      toast.error("Solo se permiten archivos PDF o DOCX");
      return;
    }

    // Check file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("El archivo es muy grande. Máximo 5MB.");
      return;
    }

    setCvFile(file);
    setUploadError(null);
    
    if (user?.email) {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);

      try {
        await axios.post(
          `${API}/candidate/upload-cv?email=${encodeURIComponent(user.email)}`,
          formData,
          { 
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000 // 60 second timeout for upload
          }
        );
        setHasProfileCV(true);
        setUploadError(null);
        toast.success("CV subido y analizado exitosamente");
      } catch (error) {
        console.error("Upload error:", error);
        setUploadError(error.response?.data?.detail || "Error al subir el CV. Intenta de nuevo.");
        toast.error(error.response?.data?.detail || "Error al subir el CV. Intenta de nuevo.");
        setCvFile(null);
      } finally {
        setUploading(false);
      }
    } else {
      toast.success(`CV "${file.name}" cargado. Ingresa tu correo para continuar.`);
    }
  };

  const retryUpload = () => {
    setCvFile(null);
    setUploadError(null);
    document.getElementById('cv-upload')?.click();
  };

  const handleOptimize = async (regenerate = false) => {
    if (!user) {
      requireEmail(() => handleOptimize(regenerate));
      return;
    }

    if (!jobDescription.trim()) {
      toast.error("Por favor ingresa la descripción del puesto");
      return;
    }

    if (!hasProfileCV && !cvFile) {
      toast.error("Por favor sube tu CV primero");
      return;
    }

    // If we have a local file but haven't uploaded to profile yet, upload first
    if (cvFile && !hasProfileCV) {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", cvFile);

      try {
        await axios.post(
          `${API}/candidate/upload-cv?email=${encodeURIComponent(user.email)}`,
          formData,
          { 
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000
          }
        );
        setHasProfileCV(true);
        toast.success("CV analizado exitosamente");
      } catch (error) {
        toast.error(error.response?.data?.detail || "Error al procesar el CV");
        setUploading(false);
        return;
      } finally {
        setUploading(false);
      }
    }

    if (regenerate && optimizationStatus.attemptsRemaining <= 0) {
      toast.error("Has alcanzado el límite de 3 intentos");
      return;
    }

    setOptimizing(true);
    try {
      const response = await axios.post(
        `${API}/candidate/optimize-cv-freeform?email=${encodeURIComponent(user.email)}&regenerate=${regenerate}`,
        { job_description: jobDescription },
        { timeout: 120000 } // 2 min timeout for AI processing
      );
      
      setOptimization(response.data.optimization);
      setOptimizationStatus({
        attemptsUsed: response.data.attempts_used,
        attemptsRemaining: response.data.attempts_remaining,
        isApproved: response.data.is_approved
      });
      setShowOptimization(true);

      if (regenerate) {
        toast.success(`Nueva optimización generada. Te quedan ${response.data.attempts_remaining} intentos.`);
      }
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("Has alcanzado el límite de 3 intentos");
      } else {
        toast.error(error.response?.data?.detail || "Error al optimizar CV");
      }
    } finally {
      setOptimizing(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await axios.post(
        `${API}/candidate/optimize-cv-freeform/approve?email=${encodeURIComponent(user.email)}&job_description=${encodeURIComponent(jobDescription)}`
      );
      setOptimizationStatus(prev => ({ ...prev, isApproved: true, attemptsRemaining: 0 }));
      toast.success("¡Optimización aprobada! Ya puedes descargar tu CV optimizado.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar");
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!user?.email || !jobDescription) return;
    
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API}/candidate/download-cv-pdf?email=${encodeURIComponent(user.email)}&job_description=${encodeURIComponent(jobDescription)}`,
        { responseType: 'blob', timeout: 60000 }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'CV_TalentMatcher.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("¡CV descargado exitosamente!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al descargar el CV");
    } finally {
      setDownloading(false);
    }
  };

  // Render full CV preview that matches PDF output
  const renderCVPreview = () => {
    if (!optimization) return null;
    
    const { 
      contact_info, 
      professional_summary, 
      key_skills, 
      work_experience, 
      education, 
      certifications, 
      languages,
      compatibility_score,
      compatibility_level,
      strengths_for_role,
      honest_assessment
    } = optimization;

    return (
      <div className="space-y-6 py-4">
        {/* Compatibility Score */}
        {compatibility_score !== undefined && (
          <div className={`p-4 rounded-lg border ${
            compatibility_score >= 70 ? 'bg-green-50 border-green-200' : 
            compatibility_score >= 50 ? 'bg-yellow-50 border-yellow-200' : 
            'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Compatibilidad con el puesto</p>
                <p className="text-sm text-muted-foreground">{honest_assessment || compatibility_level}</p>
              </div>
              <div className={`text-3xl font-bold ${
                compatibility_score >= 70 ? 'text-green-600' : 
                compatibility_score >= 50 ? 'text-yellow-600' : 
                'text-red-600'
              }`}>
                {compatibility_score}%
              </div>
            </div>
          </div>
        )}

        {/* Contact Info Header */}
        {contact_info && (
          <div className="bg-primary/5 p-4 rounded-lg">
            <h2 className="text-xl font-bold text-foreground mb-2">{contact_info.name || "Tu Nombre"}</h2>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              {contact_info.email && (
                <span className="flex items-center gap-1">
                  <Mail className="w-3 h-3" /> {contact_info.email}
                </span>
              )}
              {contact_info.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {contact_info.phone}
                </span>
              )}
              {contact_info.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {contact_info.location}
                </span>
              )}
            </div>
          </div>
        )}

        <Separator />

        {/* Professional Summary */}
        {professional_summary && (
          <div className="space-y-2">
            <h3 className="font-semibold flex items-center gap-2 text-primary">
              <User className="w-4 h-4" />
              RESUMEN PROFESIONAL
            </h3>
            <p className="text-sm leading-relaxed text-muted-foreground">{professional_summary}</p>
          </div>
        )}

        {/* Key Skills */}
        {key_skills && key_skills.length > 0 && (
          <div className="space-y-2">
            <h3 className="font-semibold flex items-center gap-2 text-primary">
              <Target className="w-4 h-4" />
              COMPETENCIAS CLAVE
            </h3>
            <div className="flex flex-wrap gap-2">
              {key_skills.slice(0, 15).map((skill, i) => (
                <Badge key={i} variant="secondary" className="text-xs">{skill}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Work Experience */}
        {work_experience && work_experience.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold flex items-center gap-2 text-primary">
              <Briefcase className="w-4 h-4" />
              EXPERIENCIA PROFESIONAL
            </h3>
            <div className="space-y-4">
              {work_experience.slice(0, 4).map((exp, i) => (
                <div key={i} className="border-l-2 border-primary/30 pl-3 py-1">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-sm">{exp.title}</p>
                      <p className="text-xs text-muted-foreground">{exp.company}</p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{exp.period}</span>
                  </div>
                  {exp.description && (
                    <p className="text-xs text-muted-foreground mt-1">{exp.description}</p>
                  )}
                  {exp.achievements && exp.achievements.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {exp.achievements.slice(0, 3).map((ach, j) => (
                        <li key={j} className="text-xs text-muted-foreground flex items-start gap-1">
                          <span className="text-primary">•</span>
                          <span>{ach}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Education */}
        {education && education.length > 0 && (
          <div className="space-y-2">
            <h3 className="font-semibold flex items-center gap-2 text-primary">
              <GraduationCap className="w-4 h-4" />
              FORMACIÓN ACADÉMICA
            </h3>
            <div className="space-y-2">
              {education.map((edu, i) => (
                <div key={i} className="flex items-start justify-between gap-2 text-sm">
                  <div>
                    <p className="font-medium">{edu.degree}</p>
                    <p className="text-xs text-muted-foreground">{edu.institution}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">{edu.year}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Certifications */}
        {certifications && certifications.length > 0 && (
          <div className="space-y-2">
            <h3 className="font-semibold flex items-center gap-2 text-primary">
              <Award className="w-4 h-4" />
              CERTIFICACIONES
            </h3>
            <div className="flex flex-wrap gap-2">
              {certifications.map((cert, i) => (
                <Badge key={i} variant="outline" className="text-xs">{cert}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Languages */}
        {languages && languages.length > 0 && (
          <div className="space-y-2">
            <h3 className="font-semibold flex items-center gap-2 text-primary">
              <Languages className="w-4 h-4" />
              IDIOMAS
            </h3>
            <div className="flex flex-wrap gap-3">
              {languages.map((lang, i) => (
                <span key={i} className="text-sm">
                  <span className="font-medium">{lang.language}</span>
                  {lang.level && <span className="text-muted-foreground"> - {lang.level}</span>}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Strengths */}
        {strengths_for_role && strengths_for_role.length > 0 && (
          <div className="space-y-2 bg-green-50 p-3 rounded-lg border border-green-200">
            <h3 className="font-semibold flex items-center gap-2 text-green-700 text-sm">
              <TrendingUp className="w-4 h-4" />
              FORTALEZAS PARA ESTE PUESTO
            </h3>
            <ul className="space-y-1">
              {strengths_for_role.map((strength, i) => (
                <li key={i} className="text-xs text-green-700 flex items-start gap-1">
                  <CheckCircle2 className="w-3 h-3 shrink-0 mt-0.5" />
                  <span>{strength}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Status */}
        <div className="flex items-center gap-2 p-3 bg-muted rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 text-muted-foreground" />
          <p className="text-muted-foreground">
            Intentos usados: {optimizationStatus.attemptsUsed}/3 
            {optimizationStatus.isApproved && " · ✓ Aprobado"}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="cv-optimizer-page">
      {/* Header */}
      <div className="text-center py-6">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-primary" />
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
          Optimiza tu CV para el puesto
        </h1>
        <p className="text-muted-foreground mt-2 max-w-xl mx-auto">
          Alinea tu experiencia con los requisitos reales del puesto al que quieres aplicar.
          <strong className="text-foreground"> Sin inventar ni exagerar.</strong>
        </p>
      </div>

      {/* Job Description Input */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Descripción del Puesto
          </CardTitle>
          <CardDescription>
            Para darte las mejores sugerencias, necesitamos la descripción real del puesto al que quieres aplicar
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs value={inputMode} onValueChange={setInputMode} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="paste" className="flex items-center gap-2">
                <Copy className="w-4 h-4" />
                Pegar descripción
              </TabsTrigger>
              <TabsTrigger value="url" className="flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                Desde enlace
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="paste" className="mt-4 space-y-3">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800 flex items-start gap-2">
                  <Lightbulb className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    <strong>Tip:</strong> Copia la descripción completa de la oferta de trabajo 
                    (desde LinkedIn, Indeed, Glassdoor, o la página de la empresa) y pégala aquí.
                  </span>
                </p>
              </div>
              <Textarea
                placeholder="Pega aquí la descripción completa del puesto...

Ejemplo:
- Título: Senior Developer
- Requisitos: 5+ años de experiencia, React, Node.js, AWS
- Responsabilidades: Liderar equipo, diseñar arquitectura, mentorear juniors
- etc."
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                rows={8}
                className="resize-none font-mono text-sm"
              />
              {jobDescription && (
                <p className="text-xs text-muted-foreground text-right">
                  {jobDescription.length} caracteres
                </p>
              )}
            </TabsContent>
            
            <TabsContent value="url" className="mt-4 space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    Algunos sitios bloquean la extracción automática. Si no funciona, 
                    usa la opción "Pegar descripción" y copia el texto manualmente.
                  </span>
                </p>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    placeholder="https://linkedin.com/jobs/... o https://indeed.com/..."
                    value={jobUrl}
                    onChange={(e) => setJobUrl(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <Button 
                  onClick={handleExtractFromUrl}
                  disabled={extracting || !jobUrl.trim()}
                  className="shrink-0"
                >
                  {extracting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Extraer
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Soportamos LinkedIn, Indeed, Glassdoor, CompuTrabajo y la mayoría de páginas de empleo
              </p>
              
              {jobDescription && inputMode === "url" && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <p className="font-medium text-green-800">Descripción extraída</p>
                  </div>
                  <p className="text-sm text-green-700 line-clamp-3">{jobDescription.substring(0, 300)}...</p>
                  <Button 
                    variant="link" 
                    className="text-green-700 p-0 h-auto mt-2"
                    onClick={() => setInputMode("paste")}
                  >
                    Ver descripción completa →
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* CV Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Tu CV Actual
          </CardTitle>
          <CardDescription>
            Sube tu CV para que podamos compararlo con los requisitos del puesto
          </CardDescription>
        </CardHeader>
        <CardContent>
          {hasProfileCV ? (
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <p className="font-medium text-green-800">CV cargado en tu perfil</p>
                <p className="text-sm text-green-600">Usaremos el CV de tu perfil para el análisis</p>
              </div>
              <Link to="/candidate/profile">
                <Button variant="outline" size="sm">Ver perfil</Button>
              </Link>
            </div>
          ) : uploadError ? (
            <div className="border-2 border-dashed border-red-300 rounded-lg p-8 text-center bg-red-50">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <p className="font-medium text-red-700">Error al subir el CV</p>
              <p className="text-sm text-red-600 mb-4">{uploadError}</p>
              <Button onClick={retryUpload} variant="outline" className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Intentar de nuevo
              </Button>
            </div>
          ) : (
            <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
              <input
                type="file"
                accept=".pdf,.docx"
                onChange={handleFileUpload}
                className="hidden"
                id="cv-upload"
                disabled={uploading}
              />
              <label htmlFor="cv-upload" className="cursor-pointer">
                {uploading ? (
                  <>
                    <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-3" />
                    <p className="font-medium text-primary">Analizando CV...</p>
                    <p className="text-sm text-muted-foreground">Esto puede tomar unos segundos</p>
                  </>
                ) : cvFile ? (
                  <>
                    <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-3" />
                    <p className="font-medium text-green-700">{cvFile.name}</p>
                    <p className="text-sm text-green-600">CV listo para analizar</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="font-medium">Subir CV</p>
                    <p className="text-sm text-muted-foreground">PDF o DOCX (máx. 5MB)</p>
                  </>
                )}
              </label>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Button */}
      <Button
        onClick={() => handleOptimize(false)}
        disabled={optimizing || !jobDescription.trim() || (!hasProfileCV && !cvFile)}
        className="w-full h-14 text-lg gap-2"
        size="lg"
      >
        {optimizing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Analizando tu CV...
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5" />
            Optimizar mi CV para este puesto
          </>
        )}
      </Button>

      {/* Info Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <Lightbulb className="w-6 h-6 text-blue-600 mb-2" />
            <p className="font-medium text-blue-900 text-sm">Mejor presentación</p>
            <p className="text-xs text-blue-700 mt-1">
              Te ayudamos a destacar tu experiencia relevante para el puesto
            </p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <Target className="w-6 h-6 text-green-600 mb-2" />
            <p className="font-medium text-green-900 text-sm">Alineado al puesto</p>
            <p className="text-xs text-green-700 mt-1">
              Adaptamos el lenguaje a lo que busca el reclutador
            </p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-4">
            <TrendingUp className="w-6 h-6 text-purple-600 mb-2" />
            <p className="font-medium text-purple-900 text-sm">Sin exageraciones</p>
            <p className="text-xs text-purple-700 mt-1">
              Solo mejoramos la presentación, nunca inventamos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Optimization Dialog - Full CV Preview */}
      <Dialog open={showOptimization} onOpenChange={setShowOptimization}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Vista Previa de tu CV Optimizado
            </DialogTitle>
            <DialogDescription>
              Así quedará tu CV. Puedes aprobar esta versión o regenerar hasta {optimizationStatus.attemptsRemaining} veces más.
            </DialogDescription>
          </DialogHeader>

          {/* Render the full CV preview */}
          {renderCVPreview()}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {!optimizationStatus.isApproved && optimizationStatus.attemptsRemaining > 0 && (
              <Button
                variant="outline"
                onClick={() => handleOptimize(true)}
                disabled={optimizing}
                className="gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${optimizing ? 'animate-spin' : ''}`} />
                Regenerar ({optimizationStatus.attemptsRemaining} restantes)
              </Button>
            )}
            
            {!optimizationStatus.isApproved ? (
              <Button onClick={handleApprove} disabled={approving} className="gap-2">
                {approving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ThumbsUp className="w-4 h-4" />
                )}
                Aprobar y generar PDF
              </Button>
            ) : (
              <Button onClick={handleDownloadPDF} disabled={downloading} className="gap-2">
                {downloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Descargar CV en PDF
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
